import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import PaginatedEndpoint from "~/util/PaginatedEndpoint";
import { SelectOption, DataWithId } from "~/types";
import { cn } from "~/lib/utils";
import { Badge } from "~/components/ui/badge";
import { Spinner } from "~/components/ui/spinner";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "~/components/ui/command";

export interface AsyncPaginatedComboboxProps<T extends DataWithId, K = T["id"]> {
  fetcher?: PaginatedEndpoint<T>;
  options?: SelectOption[];
  itemIdToOption: (id: K) => SelectOption | null;
  value: K[];
  onChange: (newValue: K[]) => void;
  selectProps?: any;
  filter?: (item: T) => boolean;
  isCreatable?: boolean;
  onCreate?: (label: string) => Promise<T>;
  idKey?: string;
  additionalSearchOptions?: { [key: string]: string };
}

const DEBOUNCE_MS = 400;

const AsyncPaginatedCombobox = observer(function <T extends DataWithId = DataWithId>(
  props: AsyncPaginatedComboboxProps<T>
) {
  const { fetcher, itemIdToOption, idKey, additionalSearchOptions, value, onChange } = props;
  const selectProps = props.selectProps || {};
  const isMulti = !!selectProps.isMulti;
  const isCreatable = props.isCreatable ?? false;
  const disabled = !!selectProps.disabled;
  const placeholder = selectProps.placeholder ?? "Nothing selected";
  const applyFilter = props.filter ?? (() => true);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const didInitialLoad = useRef(false);
  const loadingRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  const loading = fetcher?.loading ?? false;
  const allOptions =
    props.options ??
    ((fetcher
      ? fetcher.itemsList
          .filter(applyFilter)
          .map((item) => itemIdToOption(item[(idKey || "id") as keyof T] as any))
          .filter(Boolean)
      : []) as SelectOption[]);
  const query = inputValue.trim().toLowerCase();
  const options =
    query === "" ? allOptions : allOptions.filter((o) => o.label.toLowerCase().includes(query));
  const selectedOptions = value.map(itemIdToOption).filter(Boolean) as SelectOption[];
  const selectedSet = new Set(value.map((v) => String(v)));

  useEffect(() => {
    if (open && fetcher && !didInitialLoad.current && fetcher.itemsList.length === 0) {
      didInitialLoad.current = true;
      fetcher.load("", null, additionalSearchOptions);
    }
  }, [open]);

  useEffect(() => {
    if (!openRef.current || !fetcher) return;
    const t = setTimeout(() => {
      fetcher.load(inputValue, null, additionalSearchOptions);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inputValue]);

  const maybeLoadMore = (el: HTMLElement) => {
    if (!fetcher || loadingRef.current || !fetcher.hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadingRef.current = true;
      fetcher.load(inputValue, fetcher.cursor, additionalSearchOptions).finally(() => {
        loadingRef.current = false;
      });
    }
  };

  const toggle = (opt: SelectOption) => {
    if (isMulti) {
      if (selectedSet.has(String(opt.value))) {
        onChange(value.filter((v) => String(v) !== String(opt.value)));
      } else {
        onChange([...value, opt.value as any]);
      }
    } else {
      onChange([opt.value] as any);
      setOpen(false);
    }
  };

  const removeValue = (v: any) => onChange(value.filter((x) => String(x) !== String(v)));

  const handleCreate = async () => {
    if (!props.onCreate) return;
    const created = await props.onCreate(inputValue);
    if (!created) return;
    onChange([...value, created[(idKey || "id") as keyof T] as any]);
    setInputValue("");
  };

  const showCreate =
    isCreatable &&
    inputValue.trim() !== "" &&
    !options.some((o) => o.label.toLowerCase() === inputValue.trim().toLowerCase());

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setInputValue("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex flex-1 flex-wrap items-center gap-1 text-left">
            {selectedOptions.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
            {isMulti
              ? selectedOptions.map((o) => (
                  <Badge key={String(o.value)} variant="default" className="gap-1">
                    {o.label}
                    <span
                      role="button"
                      tabIndex={-1}
                      aria-label={`Remove ${o.label}`}
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removeValue(o.value);
                      }}
                    >
                      <X className="size-3" />
                    </span>
                  </Badge>
                ))
              : selectedOptions[0] && <span>{selectedOptions[0].label}</span>}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[10000] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        // The popover is portaled to <body>, so inside a Dialog it sits outside the
        // dialog's scroll lock, which cancels wheel/touch events it sees on `document`
        // — leaving the list scrollable only by dragging its scrollbar. Stopping
        // propagation here keeps those events from ever reaching that listener.
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput value={inputValue} onValueChange={setInputValue} placeholder="Search..." />
          <CommandList onScroll={(e) => maybeLoadMore(e.currentTarget)}>
            {!loading && options.length === 0 && !showCreate && <CommandEmpty>No results</CommandEmpty>}
            <CommandGroup>
              {options.map((opt) => {
                const selected = selectedSet.has(String(opt.value));
                return (
                  <CommandItem key={String(opt.value)} value={String(opt.value)} onSelect={() => toggle(opt)}>
                    <Check className={cn("size-4", selected ? "opacity-100" : "opacity-0")} />
                    <span className="flex-1">{opt.label}</span>
                  </CommandItem>
                );
              })}
              {showCreate && (
                <CommandItem value={`__create__${inputValue}`} onSelect={handleCreate}>
                  Create "{inputValue}"
                </CommandItem>
              )}
              {loading && (
                <div className="flex items-center justify-center py-2">
                  <Spinner className="size-4" />
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

export default AsyncPaginatedCombobox;
